import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';
import { RoadSign, codeOf, type RoadCode, type SignName } from './signs';

const TINT: Record<RoadCode, string> = {
  go: 'bg-go-tint text-go-ink',
  caution: 'bg-caution-tint text-caution-ink',
  info: 'bg-info-tint text-info-ink',
  hazard: 'bg-hazard-tint text-hazard-ink',
  offline: 'bg-offline-tint text-offline-ink',
};

const SOLID: Record<RoadCode, string> = {
  go: 'bg-go-fill text-go-on-fill',
  caution: 'bg-caution-fill text-caution-on-fill',
  info: 'bg-info-fill text-info-on-fill',
  hazard: 'bg-hazard-fill text-hazard-on-fill',
  offline: 'bg-offline-fill text-offline-on-fill',
};

export interface StatusPillProps {
  /** The sign; its road code picks the colours. */
  sign: SignName;
  children: ReactNode;
  /** Mono detail after the label, e.g. "64 km/h". */
  detail?: ReactNode;
  /** Solid fill for workflow states; tinted by default. */
  solid?: boolean;
  className?: string;
}

/** 26 px: sign + label (+ detail). The sign is mandatory — no state is told by colour alone. */
export function StatusPill({ sign, children, detail, solid = false, className }: StatusPillProps) {
  const code = codeOf(sign);
  return (
    <span
      className={cn(
        'inline-flex h-[26px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full pe-2.5 ps-2 text-[12px] font-bold leading-none',
        solid ? SOLID[code] : TINT[code],
        className,
      )}
    >
      <RoadSign sign={sign} size={13} />
      {children}
      {detail !== undefined && detail !== null && <span className="font-mono font-medium opacity-90">{detail}</span>}
    </span>
  );
}

export interface FilterChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  pressed: boolean;
  sign?: SignName;
  count?: ReactNode;
  size?: 'sm' | 'md';
  children?: ReactNode;
}

/** 32 px pill: label + sign + count (mono). Pressed = selected tint, selected line, selected ink. */
export function FilterChip({ pressed, sign, count, size = 'md', children, className, ...rest }: FilterChipProps) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className={cn(
        'inline-flex shrink-0 items-center gap-[7px] whitespace-nowrap rounded-full border font-bold transition-colors duration-quick',
        size === 'sm' ? 'h-[26px] px-[9px] text-[12px]' : 'h-8 px-3 text-[13px]',
        pressed ? 'border-selected-line bg-selected text-selected-ink' : 'border-line-strong bg-surface text-ink hover:border-line-input',
        className,
      )}
      {...rest}
    >
      {sign && <RoadSign sign={sign} />}
      {children}
      {count !== undefined && count !== null && (
        <span className={cn('font-mono font-medium', pressed ? 'text-selected-ink' : 'text-ink-subtle')}>{count}</span>
      )}
    </button>
  );
}

const ARABIC = /[؀-ۿ]/;

/** The vehicle's identity: a white plate, 1.5 px ink border, a 9 px info-blue band at the start. As printed, in its own script. */
export function PlateChip({ plate, size = 'md', className }: { plate: string; size?: 'sm' | 'md'; className?: string }) {
  const arabic = ARABIC.test(plate);
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-stretch overflow-hidden whitespace-nowrap rounded-[5px] border-[1.5px] border-ink bg-[var(--vlm-brand-white)] text-[color:var(--vlm-brand-asphalt)]',
        size === 'sm' ? 'h-6' : 'h-7',
        className,
      )}
    >
      <span className={cn('shrink-0 bg-info-fill', size === 'sm' ? 'w-[7px]' : 'w-[9px]')} aria-hidden="true" />
      <bdi
        lang={arabic ? 'ar' : undefined}
        dir={arabic ? 'rtl' : 'ltr'}
        className={cn(
          'grid place-items-center leading-none',
          size === 'sm' ? 'px-[7px] text-[12px]' : 'px-[9px] text-[14px]',
          arabic ? 'font-arabic font-bold tracking-[0.12em]' : 'font-mono font-semibold uppercase tracking-[var(--vlm-type-plate-tracking)]',
        )}
      >
        {plate}
      </bdi>
    </span>
  );
}

/** A change, in mono: go when it is good news, hazard when it is not. */
export function Delta({ children, good }: { children: ReactNode; good: boolean }) {
  return (
    <span className={cn('rounded-xs px-1.5 py-0.5 font-mono text-[12px] font-semibold', good ? 'bg-go-tint text-go-ink' : 'bg-hazard-tint text-hazard-ink')}>
      {children}
    </span>
  );
}

/** A small count badge over an icon button (unread notifications): caution, "needs attention". */
export function CountBadge({ count, label }: { count: number; label?: string }) {
  if (count <= 0) return null;
  return (
    <span
      aria-label={label}
      className="pointer-events-none absolute -end-0.5 -top-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-surface bg-caution-fill px-1 font-mono text-[10px] font-bold leading-none text-caution-on-fill"
    >
      <bdi dir="ltr">{count > 99 ? '99+' : count}</bdi>
    </span>
  );
}
